import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import Header from '../shared/TailwindComponents/Header';
import AISidebar from './AISidebar';
import ChatBot from './ChatBot';
import PromptBar from './PromptBar';
import { Message } from './types';
import {
  addChatGroup,
  callServiceAPI,
  getUserID,
  setChatGroupIDStore,
  researchCreateChatGroup,
  researchSendQuery,
  researchGetMessages,
  researchGetMessageContent,
  researchDeleteChatGroup,
  researchStopProcessing,
  researchListChatGroups,
  researchGetChatGroup,
  researchGetAvailableSources,
  researchUpdateChatGroup,
  researchListCollaborators,
  researchAddCollaborator,
  researchRemoveCollaborator,
  researchUpdateCollaboratorAccess,
  researchConfirmCompletion,
  ResearchSource,
} from '../../store/actions/ChatAction';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import { FileProps } from '../Files/documents/types';
import { fetchDocumentContent } from '../../store/actions/DocumentsAction';
import DocumentViewer from './DocumentViewer';
import { hideModal, showModal } from '../../store/actions/modalActions';
import { showSidePanel } from '../../store/actions/sidePanelActions';
import { fetchAILanguages } from '../../store/actions/DropdownActions';
import { getAISettings } from '../../store/actions/DataAction';
import { config } from '../../utils/config';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { getMultiHubSocketService } from '../../utils/MultiHubSocketService';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import debounce from 'lodash.debounce';
import CitationDocumentViewer from './CitationDocumentViewer';
import ResearchCitationPanel from './ResearchCitationPanel';
import clsx from 'clsx';
import ChatSkeleton from './ChatSkeleton';
import { chatWithDataSource } from '../../store/actions/AIStudioAction';
import { Tooltip } from '@mui/material';
import { PiTrashLight, PiNotePencilLight, PiEraserLight, PiXLight, PiUsersLight, PiGlobeLight, PiClockCounterClockwiseLight } from 'react-icons/pi';
import { GoHistory } from 'react-icons/go';
import Flag from '../shared/TailwindComponents/Flag';
import { useLicenseGate } from '../../hooks/useLicenseGate';

// Inline SVG globe icon for "Sources" button in header
const SourcesIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 256 256" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm88,104a87.61,87.61,0,0,1-6.4,32.94l-44.7-27.49a15.92,15.92,0,0,0-6.24-2.23l-22.82-3.08a16.11,16.11,0,0,0-16,7.86h-8.72l-3.8-7.86a15.91,15.91,0,0,0-11-8.67l-8-1.73L96.14,104h16.71a16.06,16.06,0,0,0,7.73-2l12.25-6.76a16.62,16.62,0,0,0,3-2.14l26.91-24.34A15.93,15.93,0,0,0,168,57.48V40.64A88.11,88.11,0,0,1,216,128ZM40,128a87.53,87.53,0,0,1,8.54-37.8l11.34,30.27a16,16,0,0,0,11.62,10l21.43,4.61L96.74,143a16.09,16.09,0,0,0,14.4,9h1.48l-7.23,38.61A16,16,0,0,0,109.37,208l-.37.41A88.11,88.11,0,0,1,40,128Z" />
  </svg>
);

function addStepIfNew(progress: string[], newStep: string) {
  if (progress.includes(newStep)) return progress;
  return [...progress, newStep];
}

const getWelcomeMessage = (t: (key: string) => string, service: 'research' | 'fileAnalysis' | 'myAgents'): string => {
  switch (service) {
    case 'research':
      return t('How can I help you?');
    case 'fileAnalysis':
      return t('Any files to upload?');
    case 'myAgents':
      return t('Select your custom agent');
    default:
      return t('How can I help you today?');
  }
};


interface WebPubSubResearchEvent {
  event_id: string;
  event_type: string;
  correlation_id: string;
  user_id: string;
  status: string;
  progress_percent: number;
  data: {
    message?: string;
    content?: string;
    citations?: any[];
    final_response?: string;
    follow_up_questions?: string[];
    text?: string;
    follow_up?: string[];
    chat_name?: string;
    error?: string;
    [key: string]: any;
  };
  llm_response?: string;
  error?: string;
  message?: string;
  progressPercent?: number;
  correlationId?: string;
  progress?: number;
}

const MemoizedChatBot = React.memo(ChatBot);
const MemoizedPromptBar = React.memo(PromptBar);
const NOOP_FILE_SELECT = () => {};
const NOOP_PROJECT_SELECT = () => {};

const ChatInterface: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { checkSectionGate, checkAiFeatureGate, hasAiFeatureAccess } = useLicenseGate();
  const [chatName, setChatName] = useState('');
  const [shouldTypeChatName, setShouldTypeChatName] = useState(false);
  const speechToken = config.azure.speechKey;
  const serviceRegion = config.azure.speechRegion;

  type ServiceType = 'research' | 'fileAnalysis' | 'myAgents';
  const selectedService: ServiceType  =  'research'
  const selectedIndexModelId: string | null = (null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  type SidebarView = 'Folders' | 'Projects' | 'ChatHistory' | 'AISettings' | 'Sources';
  const [sidebarView, setSidebarView] = useState<SidebarView>('ChatHistory');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [files, setFiles] = useState<(File | null)[]>([]);
  const [hasSentFiles, setHasSentFiles] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileProps | null>(null);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const { setBreadcrumbs } = useBreadcrumbs();

  const chatGroupID = useSelector((state: RootState) => state.model.chatGroupID);
  const aiSettings = useSelector((state: RootState) => state.lists.aiSettings);
  const userId = getUserID();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const { chatGroupId: chatGroupIdFromUrl } = useParams<{ chatGroupId?: string }>();
  const history = useHistory();
  const isSwitchingChat = useRef(false);
  const [isInitializingChat, setIsInitializingChat] = useState(false);
  const [isCitationViewerOpen, setIsCitationViewerOpen] = useState(false);
  const [selectedCitationUrl, setSelectedCitationUrl] = useState<string | null>(null);
  const [selectedCitationName, setSelectedCitationName] = useState<string | null>(null);
  const [selectedCitationContext, setSelectedCitationContext] = useState<{
    synthesisData?: Record<string, any>;
    retrievalData?: Record<string, any>;
  } | null>(null);
  const activeCorrelationIdRef = useRef<string | null>(null);
  const pubsubHandlerRef = useRef<((data: WebPubSubResearchEvent, eventName: string) => void) | null>(null);
  const pubsubTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentChatId = useRef<string | null>(null);
  const [isStopping, setIsStopping] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showFollowUps, setShowFollowUps] = useState(false);
  const [selectedSources, setSelectedSources] = useState<ResearchSource[]>([]);

  const getGreeting = (t: (key: string, options?: Record<string, string>) => string): string => {
    const name = userInfo.FirstName || '';
    const hour = new Date().getHours();
    if (hour < 12) return `${t('Good Morning')}, ${name}`;
    if (hour < 17) return `${t('Good Afternoon')}, ${name}`;
    if (hour < 20) return `${t('Good Evening')}, ${name}`;
    return `${t('Hello')}, ${name}`;
  };


  // Load AI settings and available sources on mount, then auto-select jurisdiction defaults
  const hasAutoSelectedSources = useRef(false);
  const isSourceSyncSkipped = useRef(false);

  useEffect(() => {
    dispatch(getAISettings());
  }, [dispatch]);

  useEffect(() => {
    if (hasAutoSelectedSources.current || selectedSources.length > 0) return;
    if (!aiSettings?.jurisdiction) return;

    const autoSelect = async () => {
      try {
        const result = await dispatch(researchGetAvailableSources());
        const jurisdictionCode = aiSettings.jurisdiction?.toUpperCase();
        const matching = (result.jurisdiction_sources || []).filter(
          (src: ResearchSource) => src.source_identifier?.toUpperCase() === jurisdictionCode
        );
        if (matching.length > 0) {
          isSourceSyncSkipped.current = true;
          setSelectedSources(matching);
          hasAutoSelectedSources.current = true;
        }
      } catch (err) {
        console.error('Failed to auto-select sources:', err);
      }
    };
    autoSelect();
  }, [aiSettings?.jurisdiction, dispatch]);

  // Sync selected sources to backend when changed for an existing chat group
  useEffect(() => {
    // Skip syncing when sources were just loaded from the backend (chat group switch or auto-select)
    if (isSourceSyncSkipped.current) {
      isSourceSyncSkipped.current = false;
      return;
    }
    if (!chatGroupID || selectedSources.length === 0) return;
    dispatch(researchUpdateChatGroup(chatGroupID, { sources: selectedSources }));
  }, [selectedSources, chatGroupID, dispatch]);

  useEffect(() => {
    return () => {
      setBreadcrumbs([]);
    };
  }, [setBreadcrumbs]);

  useEffect(() => {
    if (isSwitchingChat.current || isInitializingChat) return;
    if (chatGroupIdFromUrl && chatGroupIdFromUrl !== chatGroupID) {
      debouncedHandleSelectChatGroup(chatGroupIdFromUrl);
    } else if (!chatGroupIdFromUrl && chatGroupID) {
      dispatch(setChatGroupIDStore(''));
      setMessages([]);
      setChatName('');
      setFiles([]);
      setHasSentFiles(false);
      setBreadcrumbs([{ name: 'Research', href: '/research', current: true }]);
    }
  }, [chatGroupIdFromUrl, chatGroupID, dispatch, isInitializingChat]);

  useEffect(() => {
    if (isSidebarOpen && isCitationViewerOpen) setIsCitationViewerOpen(false);
  }, [isSidebarOpen]);

  useEffect(() => {
    if (pendingUrl) {
      history.push(pendingUrl);
      setPendingUrl(null);
    }
  }, [pendingUrl, history]);

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.isFlowDone && lastMessage?.followUps && lastMessage?.followUps?.length > 0) {
      setTimeout(() => setShowFollowUps(true), 700);
    }
  }, [messages]);

  const handleToggleSidebar = (view?: SidebarView) => {
    const targetView = view || 'ChatHistory';
    if (isSidebarOpen && sidebarView === targetView) {
      setIsSidebarOpen(false);
    } else {
      setSidebarView(targetView);
      setIsSidebarOpen(true);
    }
  };

  const handleDocumentSelect = useCallback((index: number, file: File | null) => {
    setFiles((prev) => {
      const newFiles = [...prev];
      newFiles[index] = file;
      return newFiles;
    });
  }, []);

  const handleSelectChatGroup = async (grpId: string) => {
    if (grpId === chatGroupID || isSwitchingChat.current) return;
    isSwitchingChat.current = true;
    setIsLoadingHistory(true);
    dispatch(setChatGroupIDStore(grpId));
    setIsTyping(false);
    setMessages([]);

    try {
      // Fetch chat group metadata
      const chatGroup = await dispatch(researchGetChatGroup(grpId));
      const newChatName = chatGroup?.title || 'Untitled Chat';

      // Fetch messages
      const messagesResult = await dispatch(researchGetMessages(grpId, 1, 100));
      let newMessages: Message[] = [];

      if (messagesResult?.items?.length > 0) {
        // Load content for each message from blob storage
        const messagePromises = messagesResult.items.map(async (item: any) => {
          const msgs: Message[] = [];
          if (item.role === 'user') {
            msgs.push({
              id: item.uuid + '_user',
              text: item.summary || item.content || '',
              sender: 'user',
              timestamp: new Date(item.created_at),
              disableTypingEffect: true,
              attachedFiles: [],
            });
          } else if (item.role === 'assistant') {
            // Fetch full content from blob
            let fullContent = item.summary || '';
            let citations: any[] = [];
            let synthesisData: Record<string, any> | undefined;
            let retrievalData: Record<string, any> | undefined;
            let followUps: string[] = [];
            try {
              const apiResponse = await dispatch(researchGetMessageContent(grpId, item.uuid));
              // The API wraps the blob data inside a "content" property:
              // apiResponse = { uuid, role, content: <blob_data>, status, ... }
              // blob_data  = { synthesis: { content, executive_summary, ... }, citations, ... }
              const blobData = apiResponse?.content ?? apiResponse;

              const syn = blobData?.synthesis;
              if (syn && typeof syn === 'object' && (syn.executive_summary || syn.detailed_analysis)) {
                synthesisData = syn;
              }
              // Extract retrieval data for citation side panel
              if (blobData?.retrieval && typeof blobData.retrieval === 'object') {
                retrievalData = blobData.retrieval;
              }
              // Fallback chain: synthesis.content → blob content → final_response → summary
              const rawContent =
                blobData?.synthesis?.content ||
                blobData?.content ||
                blobData?.final_response ||
                fullContent;
              // Ensure fullContent is always a string (blob may return nested objects)
              fullContent = typeof rawContent === 'string' ? rawContent : (rawContent ? JSON.stringify(rawContent) : fullContent);
              citations =
                blobData?.synthesis?.citations ||
                blobData?.citations ||
                [];
              // Normalize citations to frontend Citation interface format.
              // Handles both old raw-format blobs (source_key, content, external_url)
              // and new blobs that already use Citation keys (section, snippet, url).
              citations = citations.map((c: any, idx: number) => ({
                id: c.id || '',
                title: c.title || '',
                snippet: c.snippet || (c.content ? String(c.content).slice(0, 500) : ''),
                section: c.section || c.source_key || '',
                law: c.law || '',
                paragraph: c.paragraph || '',
                url: c.url || c.external_url || '',
                number: Number(c.number) || idx + 1,
              }));
              // Restore follow-up questions from blob
              followUps =
                blobData?.synthesis?.follow_up ||
                blobData?.follow_up ||
                blobData?.synthesis?.follow_up_questions ||
                [];
            } catch {
              // Fall back to summary
            }
            msgs.push({
              id: item.uuid + '_assistant',
              text: fullContent,
              sender: 'assistant',
              timestamp: new Date(item.created_at),
              disableTypingEffect: true,
              citations,
              followUps,
              isFlowDone: true,
              isPreFormatted: true,
              synthesisData,
              retrievalData,
              messageUuid: item.uuid,
            });
          }
          return msgs;
        });

        const messageArrays = await Promise.all(messagePromises);
        newMessages = messageArrays.flat();
      }

      // Restore sources saved on the chat group
      if (chatGroup?.sources?.length) {
        isSourceSyncSkipped.current = true;
        setSelectedSources(chatGroup.sources);
      }

      setMessages(newMessages);
      setChatName(newChatName);
      setBreadcrumbs([
        { name: 'Research', href: '/research', current: false },
        { name: newChatName, href: `/research/${grpId}`, current: true },
      ]);
      history.push(`/research/${grpId}`);
    } catch (error) {
      console.error('Error fetching chat group history:', error);
      setMessages([]);
      setChatName('');
      history.push('/research');
      setBreadcrumbs([{ name: 'Research', href: '/research', current: true }]);
    } finally {
      isSwitchingChat.current = false;
      setIsLoadingHistory(false);
    }
  };

  const handleSelectChatGroupRef = useRef(handleSelectChatGroup);
  handleSelectChatGroupRef.current = handleSelectChatGroup;

  const debouncedHandleSelectChatGroup = useCallback(
    debounce((grpId: string) => handleSelectChatGroupRef.current(grpId), 100),
    [] // stable ref — never recreated
  );

  const handleDeleteChat = (grpId: string) => {
    dispatch(
      showModal({
        type: 'confirmation',
        message: t('Delete Chat'),
        subMsg: t('This action cannot be undone.'),
        icon: <PiTrashLight className='w-6 h-6 text-red-600' />,
        isDest: true,
        onConfirm: async () => {
          try {
            dispatch(hideModal());
            await dispatch(researchDeleteChatGroup(grpId));
            setMessages([]);
            dispatch(setChatGroupIDStore(''));
            setPendingUrl('/research');
            history.push('/research')
            setBreadcrumbs([{ name: 'Research', href: '/research', current: true }]);
            dispatch(researchListChatGroups());
          } catch (err) {
            console.error('Failed to delete chat group:', err);
          }
        },
        onCancel: () => dispatch(hideModal()),
        showModal: true,
      })
    );
  };

  const handleNewChat = () => {
    setMessages([]);
    dispatch(setChatGroupIDStore(''));
    setHasSentFiles(false);
    setFiles([]);
    setChatName('');
    history.replace('/research');
    setBreadcrumbs([{ name: 'Research', href: '/research', current: true }]);
  };

  const handleNewChatWithConfirmation = () => {
    dispatch(
      showModal({
        type: 'confirmation',
        message: t('New Chat'),
        icon: <PiEraserLight className="w-6 h-6 text-gray-600" />,
        subMsg: t('Starting a new chat will clear the current conversation.'),
        onConfirm: () => {
          dispatch(hideModal());
          handleNewChat();
        },
        onCancel: () => {
          dispatch(hideModal());
        },
        showModal: true,
      })
    );
  };

  const handleFileSelect = async (f: FileProps) => {
    try {
      const content = await dispatch(fetchDocumentContent(f.FileID));
      if (content) setSelectedFile({ ...f, content });
    } catch (error) {
      if(error){
        dispatch(
          showFeedbackModal({
            modalType: 'error',
            message: t('Could not load file content. Please contact support.'),
            showModal: true,
            duration: 3000,
          })
        );
      }
      
    }
  };

  const handleSendMessage = async (text: string) => {
    if (!text.trim()) return;
    if (!checkSectionGate('research')) return;
    if (!checkAiFeatureGate('research')) return;
    const userMsgId = Date.now().toString();
    const userMsg: Message = {
      id: userMsgId,
      text,
      sender: 'user',
      timestamp: new Date(),
      attachedFiles: [],
    };
    setMessages((prev) => [...prev, userMsg]);
    setIsTyping(true);

    try {
      if (selectedService === 'research') {
        await handleSendResearchQuery(text);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages((prev) =>
        prev.map((m) =>
          m.sender === 'assistant' && m.isTyping
            ? { ...m, text: 'An error occurred. Please try again.', isTyping: false, isFlowDone: true, isPreFormatted: true }
            : m
        )
      );
      setIsTyping(false);
    }
  };

  /**
   * Send a research query via the new backend + WebPubSub flow.
   * 1. Create chat group if none exists
   * 2. POST query to backend → get correlation_id
   * 3. Subscribe to WebPubSub ai_processing hub for real-time events
   * 4. Handle progress/completion events from AI engine
   */
  const handleSendResearchQuery = async (prompt: string) => {
    setIsInitializingChat(true);
    const placeholderId = (Date.now() + 1).toString();

    // Add assistant placeholder message
    const placeholderMsg: Message = {
      id: placeholderId,
      text: '',
      sender: 'assistant',
      timestamp: new Date(),
      isTyping: true,
      progress: ['Connecting...'],
      citations: [],
      isFlowDone: false,
    };
    setMessages((prev) => [...prev, placeholderMsg]);

    try {
      // Step 1: Create chat group if this is the first message
      let activeChatUuid = chatGroupID;
      if (!activeChatUuid) {
        // Build a clean initial title (AI-generated title will replace it later)
        const initialTitle = prompt.length > 60
          ? prompt.substring(0, 57).replace(/\s+\S*$/, '') + '\u2026'
          : prompt;
        const chatGroup = await dispatch(researchCreateChatGroup({
          title: initialTitle,
          sources: selectedSources.length > 0 ? selectedSources : undefined,
        }));
        activeChatUuid = chatGroup.uuid;
        dispatch(setChatGroupIDStore(activeChatUuid));
        history.push(`/research/${activeChatUuid}`);
        setChatName(chatGroup.title || 'Untitled Chat');
        setShouldTypeChatName(true);
        setBreadcrumbs([
          { name: 'Research', href: '/research', current: false },
          { name: chatGroup.title || 'Untitled Chat', href: `/research/${activeChatUuid}`, current: true },
        ]);
      }

      // Step 2: Send query to backend
      const queryResult = await dispatch(researchSendQuery(activeChatUuid, prompt));
      const { correlation_id, message_uuid } = queryResult;
      activeCorrelationIdRef.current = correlation_id;

      // Update placeholder with message UUID  
      const assistantMsgId = `${message_uuid}_assistant`;
      setMessages((prev) =>
        prev.map((m) => m.id === placeholderId ? { ...m, id: assistantMsgId } : m)
      );

      // Step 3: Connect to WebPubSub and listen for AI processing events
      const multiHubService = getMultiHubSocketService();
      await multiHubService.connectToHub('ai_processing');

      // Clean up any previous handler
      if (pubsubHandlerRef.current) {
        multiHubService.off('ai_processing', '*', pubsubHandlerRef.current);
      }

      const handleAiEvent = (data: WebPubSubResearchEvent, eventName: string) => {
        // Only process research events matching our correlation_id
        const eventCorrelationId = data?.correlation_id || data?.correlationId;
        if (eventCorrelationId !== correlation_id) return;
        if (!eventName?.startsWith('research.')) return;

        const progressMessage = data?.data?.message || data?.message || '';
        const progressPercent = data?.progress_percent || data?.progressPercent || data?.progress || 0;
        const status = data?.status || '';

        switch (eventName) {
          case 'research.started':
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId
                  ? { ...m, progress: addStepIfNew(m.progress || [], 'Starting research...'), researchPhase: 'started' as const, researchProgress: 5 }
                  : m
              )
            );
            break;

          case 'research.orchestration': {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId
                  ? { ...m, progress: addStepIfNew(m.progress || [], progressMessage || 'Analyzing your query...'), researchPhase: 'orchestration' as const, researchProgress: progressPercent || 15 }
                  : m
              )
            );
            // AI-generated title arrives early from orchestrator
            const aiTitle = data?.data?.chat_title;
            if (aiTitle && aiTitle.length > 3 && aiTitle !== 'Orchestrator (Placeholder)') {
              setChatName(aiTitle);
              setBreadcrumbs([
                { name: 'Research', href: '/research', current: false },
                { name: aiTitle, href: `/research/${activeChatUuid}`, current: true },
              ]);
              // Update backend with the AI title
              dispatch(researchUpdateChatGroup(activeChatUuid, { title: aiTitle }));
            }
            break;
          }

          case 'research.retrieval':
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId
                  ? { ...m, progress: addStepIfNew(m.progress || [], progressMessage || 'Searching sources...'), researchPhase: 'retrieval' as const, researchProgress: progressPercent || 40 }
                  : m
              )
            );
            break;

          case 'research.evaluation': {
            // Extract thinking traces emitted by the o1 model during evaluation.
            // The PubSub event sends flat traces + source_type separately;
            // we need to wrap them into SourceThinkingTrace format and
            // accumulate across multiple evaluation source events.
            const rawTraces = data?.data?.thinking_traces;
            const evalSourceType = data?.data?.source_type || 'unknown';
            const evalDegraded = data?.data?.evaluation_degraded || data?.data?.is_degraded;
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== assistantMsgId) return m;
                let updatedTraces = m.thinkingTraces || [];
                if (rawTraces && Array.isArray(rawTraces) && rawTraces.length > 0) {
                  // Wrap flat traces into SourceThinkingTrace and append
                  updatedTraces = [
                    ...updatedTraces,
                    { source: evalSourceType, traces: rawTraces },
                  ];
                }
                return {
                  ...m,
                  progress: addStepIfNew(m.progress || [], progressMessage || 'Evaluating results...'),
                  researchPhase: 'evaluation' as const,
                  researchProgress: progressPercent || 70,
                  thinkingTraces: updatedTraces,
                  ...(evalDegraded ? { evaluationDegraded: true } : {}),
                };
              })
            );
            break;
          }

          case 'research.synthesis':
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId
                  ? { ...m, progress: addStepIfNew(m.progress || [], progressMessage || 'Writing response...'), researchPhase: 'synthesis' as const, researchProgress: progressPercent || 90 }
                  : m
              )
            );
            break;

          case 'research.completed': {
            const responseData = data?.data || {};
            const finalResponse = responseData.final_response || responseData.text || '';
            const citations = (responseData.citations || []).map((c: any) => ({ ...c, number: Number(c.number) }));
            const followUps = responseData.follow_up || [];
            const chatName = responseData.chat_name || '';
            const completedThinkingTraces = responseData.thinking_traces;
            const completedEvalDegraded = responseData.evaluation_degraded;

            // Try to parse final_response as structured synthesis JSON for rich rendering
            let synthesisData: Record<string, any> | undefined;
            if (typeof finalResponse === 'string' && finalResponse.trimStart().startsWith('{')) {
              try {
                const parsed = JSON.parse(finalResponse);
                if (parsed && typeof parsed === 'object' && (parsed.executive_summary || parsed.detailed_analysis)) {
                  synthesisData = parsed;
                }
              } catch { /* not JSON — render as markdown */ }
            }

            const liveMessage: Message = {
              id: assistantMsgId,
              text: finalResponse,
              sender: 'assistant',
              timestamp: new Date(),
              citations,
              isFlowDone: true,
              isPreFormatted: false,
              followUps,
              synthesisData,
              messageUuid: message_uuid,
              researchPhase: 'completed' as const,
              ...(completedThinkingTraces ? { thinkingTraces: completedThinkingTraces } : {}),
              ...(completedEvalDegraded ? { evaluationDegraded: true } : {}),
            };

            setMessages((prev) => {
              const idx = prev.findIndex((m) => m.id === assistantMsgId);
              if (idx !== -1) {
                const existing = prev[idx];
                const next = [...prev];
                // Preserve thinking traces from earlier evaluation events if completed event didn't include them
                next[idx] = {
                  ...liveMessage,
                  thinkingTraces: liveMessage.thinkingTraces || existing.thinkingTraces,
                  evaluationDegraded: liveMessage.evaluationDegraded || existing.evaluationDegraded,
                };
                return next;
              }
              return [...prev, liveMessage];
            });

            // Fetch retrieval data from blob in the background for citation panel
            if (message_uuid && activeChatUuid) {
              dispatch(researchGetMessageContent(activeChatUuid, message_uuid))
                .then((apiResponse: any) => {
                  const blobData = apiResponse?.content ?? apiResponse;
                  if (blobData?.retrieval && typeof blobData.retrieval === 'object') {
                    setMessages((prev) =>
                      prev.map((m) =>
                        m.id === assistantMsgId
                          ? { ...m, retrievalData: blobData.retrieval }
                          : m
                      )
                    );
                  }
                })
                .catch(() => { /* retrieval data is optional for citation panel */ });

              // Reconciliation: persist metadata to SQL via backend.
              // The AI engine no longer calls the backend directly — this is
              // the sole path for updating the message record.
              dispatch(researchConfirmCompletion(activeChatUuid, message_uuid, {
                chat_name: chatName || undefined,
                citation_count: citations.length,
                processing_time_ms: responseData.processing_time_ms,
                summary: responseData.summary,
                blob_path: responseData.blob_path,
                prompt_tokens: responseData.prompt_tokens,
                completion_tokens: responseData.completion_tokens,
                total_tokens: responseData.total_tokens,
                model_used: responseData.model_used,
              }));
            }

            if (chatName && chatName !== 'Orchestrator (Placeholder)') {
              setChatName(chatName);
              setShouldTypeChatName(true);
              setBreadcrumbs([
                { name: 'Research', href: '/research', current: false },
                { name: chatName, href: `/research/${activeChatUuid}`, current: true },
              ]);
            }

            // Cleanup
            cleanupPubSub();
            setIsInitializingChat(false);
            setIsTyping(false);

            // Refresh sidebar
            dispatch(researchListChatGroups());
            break;
          }

          case 'research.failed': {
            const errorMsg = data?.error || data?.data?.error || 'Research processing failed. Please try again.';
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId
                  ? { ...m, text: errorMsg, isTyping: false, isFlowDone: true, isPreFormatted: true }
                  : m
              )
            );
            cleanupPubSub();
            setIsInitializingChat(false);
            setIsTyping(false);
            break;
          }

          default:
            // Handle generic progress events
            if (progressMessage && status === 'in_progress') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId
                    ? { ...m, progress: addStepIfNew(m.progress || [], progressMessage) }
                    : m
                )
              );
            }
            break;
        }
      };

      pubsubHandlerRef.current = handleAiEvent;
      multiHubService.on('ai_processing', '*', handleAiEvent);

      // WebPubSub timeout: if no completion/failure event arrives within 10 minutes,
      // show a timeout error so the user isn't stuck forever
      const PUBSUB_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes (o1 evaluation can take 5-10 min)
      const timeoutId = setTimeout(() => {
        // Only trigger if we're still waiting for this specific correlation
        if (activeCorrelationIdRef.current === correlation_id) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMsgId && m.isTyping
                ? {
                    ...m,
                    text: 'The research request timed out. The AI may still be processing — please try refreshing the chat.',
                    isTyping: false,
                    isFlowDone: true,
                    isPreFormatted: true,
                  }
                : m
            )
          );
          cleanupPubSub();
          setIsInitializingChat(false);
          setIsTyping(false);
        }
      }, PUBSUB_TIMEOUT_MS);

      // Store timeout so cleanup can clear it
      pubsubTimeoutRef.current = timeoutId;

    } catch (error) {
      console.error('Error sending research query:', error);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === placeholderId
            ? { ...m, text: 'Failed to send research query. Please try again.', isTyping: false, isFlowDone: true, isPreFormatted: true }
            : m
        )
      );
      setIsInitializingChat(false);
      setIsTyping(false);
    }
  };

  const cleanupPubSub = () => {
    if (pubsubHandlerRef.current) {
      const multiHubService = getMultiHubSocketService();
      multiHubService.off('ai_processing', '*', pubsubHandlerRef.current);
      pubsubHandlerRef.current = null;
    }
    if (pubsubTimeoutRef.current) {
      clearTimeout(pubsubTimeoutRef.current);
      pubsubTimeoutRef.current = null;
    }
    activeCorrelationIdRef.current = null;
  };

  // Cleanup WebPubSub listener on unmount
  useEffect(() => {
    return () => {
      cleanupPubSub();
    };
  }, []);

  const handleStopStreamRef = useRef(async () => {});
  handleStopStreamRef.current = async () => {
    setIsStopping(true);
    setIsTyping(false);
    try {
      if (chatGroupID) {
        await dispatch(researchStopProcessing(chatGroupID));
      }
      cleanupPubSub();
      setMessages((prev: any) => {
        const lastAssistantMsg = prev.findLast((m: any) => m.sender === 'assistant' && m.isTyping);
        if (lastAssistantMsg) {
          return prev.map((m: any) =>
            m.id === lastAssistantMsg.id
              ? { ...m, isTyping: false, isFlowDone: true, isStopped: true, text: m.text || '', isPreFormatted: true }
              : m
          );
        }
        return prev;
      });
      setIsStopping(false);
    } catch (error) {
      console.error('Error stopping stream:', error);
      setIsStopping(false);
    }
  };
  const handleStopStream = useCallback(async () => {
    await handleStopStreamRef.current();
  }, []);
  

  const handleOpenCitation = useCallback((citationOrUrl: string) => {
    // Detect if this is a URL (starts with http/https or has common doc extensions)
    const isUrl = /^https?:\/\//.test(citationOrUrl) || /\.(pdf|docx?|txt)$/i.test(citationOrUrl);

    if (isUrl) {
      // URL-based citation → use old iframe viewer
      setSelectedCitationUrl(citationOrUrl);
      setSelectedCitationName(null);
      setSelectedCitationContext(null);
    } else {
      // Research citation name → find matching message context
      const lastAssistant = [...messages].reverse().find(
        (m) => m.sender === 'assistant' && m.isFlowDone
      );
      setSelectedCitationName(citationOrUrl);
      setSelectedCitationUrl(null);
      setSelectedCitationContext({
        synthesisData: lastAssistant?.synthesisData,
        retrievalData: lastAssistant?.retrievalData,
      });
    }
    setIsCitationViewerOpen(true);
  }, [messages]);

  const handleSendMessageRef = useRef(handleSendMessage);
  handleSendMessageRef.current = handleSendMessage;

  const debouncedHandleSendMessage = useCallback(
    debounce((text: string) => handleSendMessageRef.current(text), 300),
    [] // stable ref — never recreated
  );

  // Cancel debounced functions on unmount to prevent state updates on unmounted component
  useEffect(() => {
    return () => {
      debouncedHandleSendMessage.cancel();
      debouncedHandleSelectChatGroup.cancel();
    };
  }, [debouncedHandleSendMessage, debouncedHandleSelectChatGroup]);

  const handleRegenerateRef = useRef((assistantMsgId: string) => {});
  handleRegenerateRef.current = (assistantMsgId: string) => {
    const msgIndex = messages.findIndex((m) => m.id === assistantMsgId);
    if (msgIndex > 0) {
      const userMsg = messages[msgIndex - 1];
      if (userMsg.sender === 'user') {
        // Remove the old assistant message and re-send the user's query
        setMessages((prev) => prev.filter((m) => m.id !== assistantMsgId));
        handleSendResearchQuery(userMsg.text);
      }
    }
  };
  const handleRegenerate = useCallback((assistantMsgId: string) => {
    handleRegenerateRef.current(assistantMsgId);
  }, []);

  const handleCloseSidebar = useCallback(() => setIsSidebarOpen(false), []);

  const isShowingSkeleton = isLoadingHistory && messages.length === 0;
  const noMessagesYet = messages.length === 0 && !isTyping && !isLoadingHistory && !isInitializingChat;

  return (
    <div className="flex overflow-hidden h-full w-full relative" style={{ backgroundColor: '#f5f5f5' }}>
      <div className="flex flex-col flex-1 w-full relative">
        <div className="bg-white dark:bg-gray-900 flex-shrink-0">
          <Header
            breadcrumbs={
              chatName
                ? [
                    { name: 'Research', href: '/research', current: false },
                    { name: chatName, href: `/research/${chatGroupID}`, current: true },
                  ]
                : [{ name: 'Research', href: '/research', current: true }]
            }
            additionalRightElements={
              <div className="flex items-center space-x-1">
                {chatGroupID && (
                  <Tooltip title={t('New chat')}>
                    <button
                      onClick={handleNewChatWithConfirmation}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                    >
                      <PiNotePencilLight size={18} />
                    </button>
                  </Tooltip>
                )}
                {chatGroupID && (
                  <Tooltip title={t('Share')}>
                    <button
                      onClick={async () => {
                        try {
                          const collabs: any = await dispatch(researchListCollaborators(chatGroupID) as any);
                          dispatch(showSidePanel({
                            type: 'manageResourceCollaborators',
                            showSidePanel: true,
                            width: '50%',
                            message: t('Manage Access'),
                            resourceUuid: chatGroupID,
                            resourceType: 'research_chat',
                            collaborators: collabs || [],
                            currentUserUuid: userInfo?.UserUID,
                            canAdmin: true,
                            addCollaborator: async (uuid: string, userUuid: string, access: string) => {
                              await dispatch(researchAddCollaborator(uuid, userUuid, access as any) as any);
                            },
                            removeCollaborator: async (uuid: string, collabUuid: string) => {
                              await dispatch(researchRemoveCollaborator(uuid, collabUuid) as any);
                            },
                            updateAccess: async (uuid: string, collabUuid: string, access: string) => {
                              await dispatch(researchUpdateCollaboratorAccess(uuid, collabUuid, access as any) as any);
                            },
                            onUpdate: async () => {
                              dispatch(researchListChatGroups() as any);
                            },
                          }));
                        } catch (err) {
                          console.error('Failed to load collaborators:', err);
                        }
                      }}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                    >
                      <PiUsersLight size={18} />
                    </button>
                  </Tooltip>
                )}
                <Tooltip title={t('Sources')}>
                  <button
                    onClick={() => handleToggleSidebar('Sources')}
                    className={`p-2 rounded transition-colors ${isSidebarOpen && sidebarView === 'Sources' ? 'text-blue-600 bg-blue-50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                  >
                    <PiGlobeLight size={18} />
                  </button>
                </Tooltip>
                <Tooltip title={t('History')}>
                  <button
                    onClick={() => handleToggleSidebar('ChatHistory')}
                    className={`p-2 rounded transition-colors ${isSidebarOpen && sidebarView === 'ChatHistory' ? 'text-blue-600 bg-blue-50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                  >
                    <PiClockCounterClockwiseLight size={18} />
                  </button>
                </Tooltip>
              </div>
            }
          />
        </div>
        {isShowingSkeleton ? (
          <div className="flex flex-1 overflow-hidden">
            <div className="flex flex-1 flex-col overflow-hidden">
              <ChatSkeleton />
              <div className="w-2/3 self-center">
                <MemoizedPromptBar
                  onSend={debouncedHandleSendMessage}
                  speechToken={speechToken}
                  serviceRegion={serviceRegion}
                  selectedService={selectedService}
                  files={files}
                  onFileSelect={handleDocumentSelect}
                  hasSentFiles={hasSentFiles}
                  setHasSentFiles={setHasSentFiles}
                  setFiles={setFiles}
                  isTyping={isTyping || isInitializingChat || isStopping}
                  onStop={handleStopStream}
                  disabled={!hasAiFeatureAccess('research')}
                />
              </div>
            </div>
          </div>
        ) : noMessagesYet ? (
          <div className="flex-1 flex flex-col items-center justify-center">
            <h1 className="text-2xl font-semibold text-gray-700 dark:text-gray-200">{getGreeting(t)}</h1>
            <h2 className="text-2xl mb-2 font-semibold text-gray-500 dark:text-gray-300">{getWelcomeMessage(t, selectedService)}</h2>
            <div className="w-full max-w-xl mt-5">
              <MemoizedPromptBar
                onSend={debouncedHandleSendMessage}
                speechToken={speechToken}
                serviceRegion={serviceRegion}
                selectedService={selectedService}
                files={files}
                onFileSelect={handleDocumentSelect}
                hasSentFiles={hasSentFiles}
                setHasSentFiles={setHasSentFiles}
                setFiles={setFiles}
                isTyping={isTyping || isInitializingChat || isStopping}
                onStop={handleStopStream}
                disabled={!hasAiFeatureAccess('research')}
              />
            </div>
            {/* Selected sources chips */}
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
              {selectedSources.map((src) => (
                <span
                  key={`chip-${src.source_type}-${src.source_identifier}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200"
                >
                  {src.source_type !== 'user_datasource' && (
                    <Flag countryCode={src.source_identifier} size="sm" />
                  )}
                  {src.source_name || src.source_identifier}
                  <button
                    onClick={() => setSelectedSources((prev) => prev.filter((s) => !(s.source_type === src.source_type && s.source_identifier === src.source_identifier)))}
                    className="ml-0.5 hover:text-blue-900 transition-colors"
                  >
                    <PiXLight className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <button
                onClick={() => handleToggleSidebar('Sources')}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400 hover:bg-white bg-transparent text-xs transition-colors"
              >
                <SourcesIcon size={14} />
                {selectedSources.length > 0 ? t('Edit sources') : t('Select sources')}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-1 overflow-hidden">
            <div className="flex flex-1 flex-col overflow-hidden">
              <MemoizedChatBot
                messages={messages}
                setMessages={setMessages}
                isTyping={isTyping}
                showPrompts={false}
                onOpenCitation={handleOpenCitation}
                onSendMessage={debouncedHandleSendMessage}
                onFileSelect={NOOP_FILE_SELECT}
                files={files}
                speechToken={speechToken}
                serviceRegion={serviceRegion}
                aiSettings={aiSettings}
                className="flex-1 overflow-hidden"
                onRegenerate={handleRegenerate}
              />
              <div className="w-2/3 self-center mb-1">
                {showFollowUps && messages[messages.length - 1]?.followUps && (
                  <div className="absolute bottom-40 left-0 right-0 flex justify-center space-x-2 px-4 z-10">
                    {messages[messages.length - 1].followUps?.map((followUp, idx) => (
                      <Tooltip title={followUp}>
                      <button
                        key={idx}
                        className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-full shadow-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-300 slide-in text-xs max-w-64 truncate"
                        style={{ animationDelay: `${idx * 100 + 700}ms` }}
                        onClick={() => debouncedHandleSendMessage(followUp)}
                        >
                        {followUp}
                      </button>
                        </Tooltip>
                    ))}
                  </div>
                )}
                <MemoizedPromptBar
                  onSend={debouncedHandleSendMessage}
                  speechToken={speechToken}
                  serviceRegion={serviceRegion}
                  selectedService={selectedService}
                  files={files}
                  onFileSelect={handleDocumentSelect}
                  hasSentFiles={hasSentFiles}
                  setHasSentFiles={setHasSentFiles}
                  setFiles={setFiles}
                  isTyping={isTyping || isInitializingChat || isStopping}
                  onStop={handleStopStream}
                  disabled={!hasAiFeatureAccess('research')}
                />
                {/* Source chips below prompt bar in active chat */}
                {selectedSources.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {selectedSources.map((src) => (
                      <span
                        key={`active-chip-${src.source_type}-${src.source_identifier}`}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-medium border border-blue-200"
                      >
                        {src.source_type !== 'user_datasource' && (
                          <Flag countryCode={src.source_identifier} size="sm" />
                        )}
                        {src.source_name || src.source_identifier}
                        <button
                          onClick={() => setSelectedSources((prev) => prev.filter((s) => !(s.source_type === src.source_type && s.source_identifier === src.source_identifier)))}
                          className="ml-0.5 hover:text-blue-900 transition-colors"
                        >
                          <PiXLight className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    <button
                      onClick={() => handleToggleSidebar('Sources')}
                      className="text-[11px] text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {t('Edit')}
                    </button>
                  </div>
                )}
              </div>
            </div>
            {selectedFile && (
              <DocumentViewer file={selectedFile} onClose={() => setSelectedFile(null)} onUpdateFile={(upd) => setSelectedFile(upd)} />
            )}
            {isCitationViewerOpen && selectedCitationUrl && (
              <div
                className={clsx(
                  'w-1/2 h-full border-l border-gray-200 dark:border-gray-700 transition-transform duration-300 ease-in-out',
                  isCitationViewerOpen ? 'translate-x-0' : 'translate-x-full'
                )}
              >
                <CitationDocumentViewer
                  url={selectedCitationUrl}
                  onClose={() => setIsCitationViewerOpen(false)}
                />
              </div>
            )}
            {isCitationViewerOpen && selectedCitationName && (
              <div
                className={clsx(
                  'w-1/2 h-full border-l border-gray-200 dark:border-gray-700 transition-transform duration-300 ease-in-out',
                  isCitationViewerOpen ? 'translate-x-0' : 'translate-x-full'
                )}
              >
                <ResearchCitationPanel
                  citationName={selectedCitationName}
                  synthesisData={selectedCitationContext?.synthesisData}
                  retrievalData={selectedCitationContext?.retrievalData}
                  onClose={() => setIsCitationViewerOpen(false)}
                />
              </div>
            )}
          </div>
        )}
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 self-center my-1">
          {t('PONS AI V1.7 - Built to support, not supplant, professional judgment. Confirm critical findings.')}
        </p>
      </div>
      {isSidebarOpen && (
        <AISidebar
          onFileSelect={handleFileSelect}
          currentView={sidebarView}
          onSelectChatGroup={debouncedHandleSelectChatGroup}
          handleDeleteChat={handleDeleteChat}
          onProjectSelect={NOOP_PROJECT_SELECT}
          selectedSources={selectedSources}
          onSourcesChange={setSelectedSources}
          onClose={handleCloseSidebar}
          isChatLoading={isLoadingHistory}
        />
      )}
    </div>
  );
};

export default ChatInterface;